import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../../providers/AuthContext";
import InputField from "../InputField";
import Dropdown from "../Dropdown";
import Modal from "../Modal";
import ConfirmationModal from "../ConfirmationModal";
import {
  getScoCustomQuestions,
  createScoCustomQuestion,
  updateScoCustomQuestion,
  deleteScoCustomQuestion,
} from "../../api/Tenants";

const QUESTION_TYPES = ["Text", "Checkbox", "Dropdown"];

const EMPTY_FORM = {
  scoCustomQuestionPublicId: "",
  label: "",
  questionType: "Text",
  optionsText: "",
  sortOrder: 0,
};

/**
 * Administers a tenant's customer-specific SCO form questions (PBI #320): the optional
 * questions shown on the customer's onboarding and edit forms, whose answers are mailed
 * to Diflex with the case. Free text, checkbox and dropdown are supported; dropdown
 * options are entered one per line.
 */
const ScoCustomQuestionsSection = ({ tenantPublicId }) => {
  const { getAccessToken } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteState, setDeleteState] = useState({ isOpen: false, question: null });

  const loadQuestions = useCallback(async () => {
    try {
      const accessToken = await getAccessToken();
      const loaded = await getScoCustomQuestions(tenantPublicId, accessToken);

      setQuestions(loaded || []);
    } catch (error) {
      console.error("Failed to fetch SCO custom questions:", error?.message ?? error);
    }
  }, [tenantPublicId, getAccessToken]);

  useEffect(() => {
    if (tenantPublicId) loadQuestions();
  }, [tenantPublicId, loadQuestions]);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, sortOrder: questions.length });
    setIsModalOpen(true);
  };

  const openEdit = (question) => {
    setForm({
      scoCustomQuestionPublicId: question.scoCustomQuestionPublicId,
      label: question.label,
      questionType: question.questionType,
      optionsText: (question.options || []).join("\n"),
      sortOrder: question.sortOrder,
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (isSaving) return;

    if (!form.label.trim()) {
      toast.error("The question needs a label.");
      return;
    }

    const options =
      form.questionType === "Dropdown"
        ? form.optionsText
            .split("\n")
            .map((option) => option.trim())
            .filter(Boolean)
        : [];

    if (form.questionType === "Dropdown" && options.length === 0) {
      toast.error("A dropdown question needs at least one option.");
      return;
    }

    setIsSaving(true);

    try {
      const accessToken = await getAccessToken();

      if (form.scoCustomQuestionPublicId) {
        await updateScoCustomQuestion(
          {
            scoCustomQuestionPublicId: form.scoCustomQuestionPublicId,
            label: form.label.trim(),
            questionType: form.questionType,
            options,
            sortOrder: Number(form.sortOrder) || 0,
          },
          accessToken
        );
        toast.success("Question updated");
      } else {
        await createScoCustomQuestion(
          {
            tenantPublicId,
            label: form.label.trim(),
            questionType: form.questionType,
            options,
            sortOrder: Number(form.sortOrder) || 0,
          },
          accessToken
        );
        toast.success("Question created");
      }

      setIsModalOpen(false);
      await loadQuestions();
    } catch (error) {
      console.error("Failed to save SCO custom question:", error?.message ?? error);
      toast.error(error.message || "Failed to save the question");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    const question = deleteState.question;

    setDeleteState({ isOpen: false, question: null });

    if (!question) return;

    try {
      const accessToken = await getAccessToken();

      await deleteScoCustomQuestion(question.scoCustomQuestionPublicId, accessToken);
      toast.success("Question deleted");
      await loadQuestions();
    } catch (error) {
      console.error("Failed to delete SCO custom question:", error?.message ?? error);
      toast.error(error.message || "Failed to delete the question");
    }
  };

  return (
    <div className="flex flex-col border-t">
      <div className="flex flex-row items-center py-4">
        <h2 className="">SCO questions</h2>
        <div className="flex gap-4 ml-auto">
          <button className="btn btn-primary-small" onClick={openCreate}>
            Add question
          </button>
        </div>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Customer-specific questions shown on the customer's onboarding and edit forms.
        The answers are included in the case mail — nothing acts on them automatically.
      </p>

      {questions.length === 0 ? (
        <p className="text-sm text-gray-600 mb-4">No questions configured.</p>
      ) : (
        <table className="w-full text-sm mb-4">
          <thead>
            <tr className="text-left border-b">
              <th className="py-2">Question</th>
              <th className="py-2">Type</th>
              <th className="py-2">Options</th>
              <th className="py-2">Sort order</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {questions.map((question) => (
              <tr key={question.scoCustomQuestionPublicId} className="border-b">
                <td className="py-2 pr-4">{question.label}</td>
                <td className="py-2 pr-4">{question.questionType}</td>
                <td className="py-2 pr-4">
                  {(question.options || []).join(", ") || "-"}
                </td>
                <td className="py-2 pr-4">{question.sortOrder}</td>
                <td className="py-2 text-right whitespace-nowrap">
                  <button
                    className="text-primary-400 hover:underline mr-4"
                    onClick={() => openEdit(question)}
                  >
                    Edit
                  </button>
                  <button
                    className="text-error-600 hover:underline"
                    onClick={() => setDeleteState({ isOpen: true, question })}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal isOpen={isModalOpen} setIsOpen={setIsModalOpen}>
        <h2 className="text-xl font-semibold mb-4">
          {form.scoCustomQuestionPublicId ? "Edit question" : "Add question"}
        </h2>
        <div className="flex flex-col gap-4">
          <InputField
            label="Question"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="E.g. Should the user be added to the door access system?"
            type="text"
          />

          <Dropdown
            label="Type"
            value={form.questionType}
            options={QUESTION_TYPES}
            onSelect={(option) => setForm({ ...form, questionType: option })}
            placeholder="Choose type"
          />

          {form.questionType === "Dropdown" && (
            <div className="flex flex-col">
              <label className="text-sm font-bold pb-2">
                Options (one per line)
              </label>
              <textarea
                className="border rounded p-2 text-sm min-h-[100px]"
                value={form.optionsText}
                onChange={(e) => setForm({ ...form, optionsText: e.target.value })}
                placeholder={"Option 1\nOption 2"}
              />
            </div>
          )}

          <InputField
            label="Sort order"
            value={String(form.sortOrder)}
            onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
            placeholder="0"
            type="number"
          />

          <div className="flex gap-4 justify-end">
            <button
              className="btn btn-outline-primary-small"
              onClick={() => setIsModalOpen(false)}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary-small"
              onClick={handleSave}
              disabled={isSaving}
            >
              Save
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={deleteState.isOpen}
        title={`Delete the question "${deleteState.question?.label || ""}"?`}
        onClose={() => setDeleteState({ isOpen: false, question: null })}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default ScoCustomQuestionsSection;
